import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { authApi, UserProfile } from '../../services/authApi';

const ManageAccount: React.FC = () => {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [passwordFormData, setPasswordFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteEmail, setDeleteEmail] = useState('');
  const [passwordErrors, setPasswordErrors] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [deleteError, setDeleteError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Only load profile once
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      const loadProfile = async () => {
        try {
          const userProfile = await authApi.getProfile();
          console.log('🔍 Loaded profile:', userProfile);
          setProfile(userProfile);
        } catch (error) {
          console.error('Failed to load profile:', error);
        } finally {
          setLoading(false);
        }
      };
      loadProfile();
    }
  }, [isAuthenticated, navigate]);

  const validatePassword = (password: string): boolean => {
    // At least 8 characters, one letter, one number
    const hasMinLength = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    return hasMinLength && hasLetter && hasNumber;
  };

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (passwordErrors[name as keyof typeof passwordErrors]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }

    // Clear success message when user starts typing
    if (passwordChangeSuccess) {
      setPasswordChangeSuccess(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    // Validate old password
    if (!passwordFormData.oldPassword) {
      newErrors.oldPassword = 'Current password is required.';
    }

    // Validate new password
    if (!passwordFormData.newPassword) {
      newErrors.newPassword = 'New password is required.';
    } else if (!validatePassword(passwordFormData.newPassword)) {
      newErrors.newPassword = 'Password must be at least 8 characters long with at least one letter and one number.';
    }

    // Validate confirm password
    if (!passwordFormData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password.';
    } else if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    // Check if new password is same as old password
    if (passwordFormData.oldPassword && passwordFormData.newPassword && 
        passwordFormData.oldPassword === passwordFormData.newPassword) {
      newErrors.newPassword = 'New password must be different from your current password.';
    }

    setPasswordErrors(newErrors);

    // If no errors, proceed with password change
    if (!newErrors.oldPassword && !newErrors.newPassword && !newErrors.confirmPassword) {
      setIsChangingPassword(true);
      setPasswordChangeSuccess(false);

      try {
        await authApi.changePassword({
          oldPassword: passwordFormData.oldPassword,
          newPassword: passwordFormData.newPassword,
        });

        setPasswordChangeSuccess(true);
        setPasswordFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        });

        // Clear success message after 5 seconds
        setTimeout(() => {
          setPasswordChangeSuccess(false);
        }, 5000);
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to change password. Please try again.';
        if (errorMessage.includes('Current password is incorrect') || errorMessage.includes('incorrect')) {
          setPasswordErrors(prev => ({
            ...prev,
            oldPassword: 'Current password is incorrect.',
          }));
        } else if (errorMessage.includes('does not have a password')) {
          setPasswordErrors(prev => ({
            ...prev,
            oldPassword: 'This account does not have a password. It was created with Google Sign-In.',
          }));
        } else {
          setPasswordErrors(prev => ({
            ...prev,
            newPassword: errorMessage,
          }));
        }
      } finally {
        setIsChangingPassword(false);
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      setDeleteError('');
      return;
    }

    const password = profile?.hasPassword ? deletePassword : undefined;
    const email = profile?.hasGoogleId ? deleteEmail : undefined;

    // Validate password if required
    if (profile?.hasPassword && !password) {
      setDeleteError('Password is required to delete your account.');
      return;
    }

    // Validate email if required (Google Sign-In users)
    if (profile?.hasGoogleId && !email) {
      setDeleteError('Email is required to delete your account.');
      return;
    }

    // Validate email matches user's email
    if (profile?.hasGoogleId && email && email !== profile.email) {
      setDeleteError('Email does not match your account email.');
      return;
    }

    setIsDeletingAccount(true);
    setDeleteError('');

    try {
      await authApi.deleteAccount({ password, email });

      // Logout and redirect to home
      logout();
      navigate('/daily');
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to delete account. Please try again.';
      if (errorMessage.includes('Password is incorrect') || errorMessage.includes('incorrect')) {
        setDeleteError('Password is incorrect.');
      } else if (errorMessage.includes('Password is required')) {
        setDeleteError('Password is required to delete your account.');
      } else if (errorMessage.includes('Email does not match') || errorMessage.includes('email')) {
        setDeleteError('Email does not match your account email.');
      } else {
        setDeleteError(errorMessage);
      }
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePassword('');
    setDeleteEmail('');
    setDeleteError('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-light-secondary dark:text-text-secondary">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />
      
      <main className="flex-grow max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-text-light-primary dark:text-text-primary mb-2">
            Manage Account
          </h1>
          <p className="text-sm text-text-light-secondary dark:text-text-secondary mb-8">
            {profile?.email}
          </p>

          {/* Account Type Section */}
          <section className="mb-8 pb-8 border-b border-border-light dark:border-border-color">
            <h2 className="text-xl font-semibold text-text-light-primary dark:text-text-primary mb-4">
              Account Type
            </h2>
            {profile?.hasGoogleId ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-600 p-4">
                <p className="text-blue-800 dark:text-blue-200">
                  <strong>Google Sign-In Account</strong>
                  <br />
                  <span className="text-sm">Your account was created using Google Sign-In. Password changes are not available for Google accounts.</span>
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-400 dark:border-gray-600 p-4">
                <p className="text-gray-800 dark:text-gray-200">
                  <strong>Email and Password Account</strong>
                  <br />
                  <span className="text-sm">Your account was created with an email and password.</span>
                </p>
              </div>
            )}
          </section>

          {/* Change Password Section - Only for email/password accounts */}
          {profile?.hasPassword && (
            <section className="mb-8 pb-8 border-b border-border-light dark:border-border-color">
              <h2 className="text-xl font-semibold text-text-light-primary dark:text-text-primary mb-4">
                Change Password
              </h2>

              {passwordChangeSuccess && (
                <div className="mb-4 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 dark:border-green-600 p-4">
                  <p className="text-green-800 dark:text-green-200">
                    Password changed successfully!
                  </p>
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                  <label htmlFor="oldPassword" className="block text-sm font-medium text-text-light-primary dark:text-text-primary mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="oldPassword"
                    name="oldPassword"
                    value={passwordFormData.oldPassword}
                    onChange={handlePasswordInputChange}
                    className={`w-full px-4 py-2 rounded-md border ${
                      passwordErrors.oldPassword
                        ? 'border-red-500 dark:border-red-600'
                        : 'border-border-light dark:border-border-color'
                    } bg-white dark:bg-background-card text-text-light-primary dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Enter your current password"
                  />
                  {passwordErrors.oldPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.oldPassword}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-text-light-primary dark:text-text-primary mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordFormData.newPassword}
                    onChange={handlePasswordInputChange}
                    className={`w-full px-4 py-2 rounded-md border ${
                      passwordErrors.newPassword
                        ? 'border-red-500 dark:border-red-600'
                        : 'border-border-light dark:border-border-color'
                    } bg-white dark:bg-background-card text-text-light-primary dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Enter your new password"
                  />
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.newPassword}</p>
                  )}
                  <p className="mt-1 text-xs text-text-light-secondary dark:text-text-secondary">
                    Must be at least 8 characters with at least one letter and one number.
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-light-primary dark:text-text-primary mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordFormData.confirmPassword}
                    onChange={handlePasswordInputChange}
                    className={`w-full px-4 py-2 rounded-md border ${
                      passwordErrors.confirmPassword
                        ? 'border-red-500 dark:border-red-600'
                        : 'border-border-light dark:border-border-color'
                    } bg-white dark:bg-background-card text-text-light-primary dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-primary`}
                    placeholder="Confirm your new password"
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{passwordErrors.confirmPassword}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="w-full bg-primary dark:bg-highlight-gold text-gray-900 dark:text-gray-900 px-4 py-2 rounded-md font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isChangingPassword ? 'Changing Password...' : 'Change Password'}
                </button>
              </form>
            </section>
          )}

          {/* Delete Account Section */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-light-primary dark:text-text-primary mb-4">
              Delete Account
            </h2>
            {profile?.hasProSubscription ? (
              <>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 mb-4">
                  <p className="text-yellow-800 dark:text-yellow-200 mb-2">
                    <strong>Account deletion is not available while you have an active Pro Plan subscription.</strong>
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    You will not be able to delete your account until your Pro Plan subscription ends on {profile.proSubscriptionEndDate ? new Date(profile.proSubscriptionEndDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : 'N/A'}.
                  </p>
                </div>
                <button
                  disabled
                  className="w-full bg-gray-400 dark:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold cursor-not-allowed opacity-50"
                >
                  Delete Account
                </button>
              </>
            ) : (
              <>
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600 p-4 mb-4">
                  <p className="text-red-800 dark:text-red-200 mb-2">
                    <strong>Warning: This action cannot be undone.</strong>
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    Deleting your account will permanently remove all your data, including your ticker lists, preferences, and account information.
                  </p>
                </div>

                {!showDeleteConfirm ? (
                  <button
                    onClick={handleDeleteAccount}
                    className="w-full bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-md font-semibold hover:bg-red-700 dark:hover:bg-red-800 transition-colors"
                  >
                    Delete Account
                  </button>
                ) : (
              <div className="space-y-4">
                {profile?.hasPassword && (
                  <div>
                    <label htmlFor="deletePassword" className="block text-sm font-medium text-text-light-primary dark:text-text-primary mb-2">
                      Enter your password to confirm
                    </label>
                    <input
                      type="password"
                      id="deletePassword"
                      value={deletePassword}
                      onChange={(e) => {
                        setDeletePassword(e.target.value);
                        setDeleteError('');
                      }}
                      className={`w-full px-4 py-2 rounded-md border ${
                        deleteError
                          ? 'border-red-500 dark:border-red-600'
                          : 'border-border-light dark:border-border-color'
                      } bg-white dark:bg-background-card text-text-light-primary dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-red-500`}
                      placeholder="Enter your password"
                    />
                    {deleteError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{deleteError}</p>
                    )}
                  </div>
                )}

                {profile?.hasGoogleId && (
                  <div>
                    <label htmlFor="deleteEmail" className="block text-sm font-medium text-text-light-primary dark:text-text-primary mb-2">
                      Enter your email to confirm
                    </label>
                    <input
                      type="email"
                      id="deleteEmail"
                      value={deleteEmail}
                      onChange={(e) => {
                        setDeleteEmail(e.target.value);
                        setDeleteError('');
                      }}
                      className={`w-full px-4 py-2 rounded-md border ${
                        deleteError
                          ? 'border-red-500 dark:border-red-600'
                          : 'border-border-light dark:border-border-color'
                      } bg-white dark:bg-background-card text-text-light-primary dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-red-500`}
                      placeholder="Enter your email"
                    />
                    {deleteError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{deleteError}</p>
                    )}
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeletingAccount || (profile?.hasPassword && !deletePassword) || (profile?.hasGoogleId && !deleteEmail)}
                    className="flex-1 bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-md font-semibold hover:bg-red-700 dark:hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeletingAccount ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                  <button
                    onClick={handleCancelDelete}
                    disabled={isDeletingAccount}
                    className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-md font-semibold hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>
              </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ManageAccount;

